import { Link } from 'react-router-dom';
import {
  Monitor,
  MoreVertical,
  Printer,
  ScanLine,
  Store,
  Tablet,
  CreditCard,
  Box,
} from 'lucide-react';
import MaterielBadges from './MaterielBadges';
import {
  activityFromItem,
  capabilityLabels,
  deviceTitle,
  pointOfSaleLabel,
  typeLabel,
} from '../../materiels/materielDisplayMaps';

function TypeIcon({ type }) {
  const cls = 'h-5 w-5 text-primary-600 shrink-0';
  switch (type) {
    case 'PRINTER_CLOUD':
    case 'PRINTER_LOCAL':
      return <Printer className={cls} aria-hidden />;
    case 'SCANNER':
      return <ScanLine className={cls} aria-hidden />;
    case 'PAYMENT_TERMINAL':
      return <CreditCard className={cls} aria-hidden />;
    case 'POS':
      return <Box className={cls} aria-hidden />;
    case 'DISPLAY':
      return <Monitor className={cls} aria-hidden />;
    case 'TABLET':
      return <Tablet className={cls} aria-hidden />;
    default:
      return <Box className={cls} aria-hidden />;
  }
}

/**
 * Ligne dense parc Matériels V2 — aucune ID technique affichée.
 */
export default function MaterielListRow({
  item,
  selected = false,
  onSelect,
  showPointOfSale = true,
  menu = null,
}) {
  const title = deviceTitle(item);
  const typeUi = typeLabel(item?.type);
  const sn = item?.serialNumber ? String(item.serialNumber) : '—';
  const caps = capabilityLabels(item?.capabilities).slice(0, 3);
  const pdv = pointOfSaleLabel(item);
  const activity = activityFromItem(item);
  const hasPdvLink = Boolean(item?.commercant?.nom && item?.commercantId);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect?.(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect?.(item);
        }
      }}
      className={`flex items-start gap-3 px-3 py-3 border-b border-gray-100 cursor-pointer transition-colors ${
        selected ? 'bg-primary-50' : 'bg-white hover:bg-gray-50'
      }`}
    >
      <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 border border-primary-100">
        <TypeIcon type={item?.type} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium text-gray-900 truncate">{title}</p>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-primary-50 text-primary-800 border border-primary-100">
            {typeUi}
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-0.5 truncate">
          SN · {sn}
          {caps.length > 0 ? ` · ${caps.join(' · ')}` : ''}
        </p>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          {showPointOfSale ? (
            <span className="inline-flex items-center gap-1 text-xs text-gray-600 min-w-0">
              <Store className="h-3.5 w-3.5 text-gray-400 shrink-0" aria-hidden />
              {hasPdvLink ? (
                <Link
                  to={`/commercants/${item.commercantId}`}
                  onClick={(e) => e.stopPropagation()}
                  className="truncate text-primary-700 hover:underline"
                >
                  {pdv}
                </Link>
              ) : (
                <span className="truncate">{pdv}</span>
              )}
            </span>
          ) : null}
          <span className="text-xs text-gray-400 truncate">{activity}</span>
        </div>
      </div>

      <div className="flex flex-col items-end gap-2 shrink-0">
        <MaterielBadges item={item} />
        {menu ? (
          <div onClick={(e) => e.stopPropagation()}>{menu}</div>
        ) : (
          <MoreVertical className="h-4 w-4 text-gray-300" aria-hidden />
        )}
      </div>
    </div>
  );
}
